export const ARTICLES_LEGAL = [
  {
    path: "/cadre-legal/anj",
    category: "Cadre légal",
    title: "L'ANJ : l'Autorité nationale des jeux, rôle et missions",
    description:
      "Rôle, missions et pouvoirs de l'Autorité nationale des jeux (ANJ) : agréments, contrôle des opérateurs, protection des joueurs.",
    h1: "L'ANJ, l'Autorité nationale des jeux",
    intro:
      "L'Autorité nationale des jeux (ANJ) est l'autorité administrative indépendante chargée de réguler l'ensemble des jeux d'argent et de hasard en France. Elle a succédé en 2020 à l'ARJEL, qui régulait uniquement les jeux en ligne.",
    sections: [
      {
        id: "missions",
        h2: "Ses missions",
        blocks: [
          {
            t: "list",
            items: [
              "Délivrer les agréments aux opérateurs de jeux et paris en ligne.",
              "Contrôler le respect de la réglementation par les opérateurs.",
              "Protéger les joueurs : prévention du jeu excessif, protection des mineurs, lutte contre l'addiction.",
              "Lutter contre les sites illégaux, notamment par le blocage administratif de sites non agréés.",
              "Veiller à l'intégrité des compétitions sportives et à la lutte contre le blanchiment et la fraude.",
              "Encadrer la communication commerciale des opérateurs.",
              "Tenir le fichier des interdits de jeux.",
            ],
          },
        ],
      },
      {
        id: "jeux-concernes",
        h2: "Quels jeux sont concernés ?",
        blocks: [
          {
            t: "p",
            html: "En ligne, seuls sont autorisés, pour les opérateurs agréés : <strong>les paris sportifs, les paris hippiques et le poker</strong>. Les jeux de tirage et de grattage restent sous droits exclusifs de la Française des Jeux et les paris hippiques en réseau physique sous ceux du PMU. <strong>Le casino en ligne n'est pas autorisé en France.</strong>",
          },
        ],
      },
      {
        id: "liste-officielle",
        h2: "Liste officielle des opérateurs",
        blocks: [
          {
            t: "p",
            html: "L'ANJ publie la liste officielle des opérateurs agréés, mise à jour régulièrement. Elle est la seule référence fiable.",
          },
          {
            t: "p",
            html: '<a href="https://anj.fr" target="_blank" rel="noopener noreferrer nofollow" class="font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]">Consulter le site officiel de l\'ANJ (anj.fr)</a>',
          },
        ],
      },
      {
        id: "site-suspect",
        h2: "Que faire face à un site suspect ?",
        blocks: [
          {
            t: "p",
            html: "L'ANJ permet de signaler un site de jeux d'argent non autorisé sur son site officiel.",
          },
        ],
      },
      {
        id: "droits-du-joueur",
        h2: "L'ANJ et le joueur : ses droits",
        blocks: [
          {
            t: "p",
            html: "Le joueur peut : demander l'interdiction volontaire de jeux, réclamer en cas de litige avec un opérateur (d'abord auprès de l'opérateur, puis en saisissant les autorités compétentes), et accéder à l'information sur le jeu responsable.",
          },
          {
            t: "callout",
            kind: "retain",
            html: "Les informations de cette page sont générales. Pour toute situation précise, référez-vous aux informations officielles de l'ANJ.",
          },
        ],
      },
    ],
  },
  {
    path: "/cadre-legal/site-legal-ou-illegal",
    category: "Cadre légal",
    title: "Comment reconnaître un site de paris sportifs légal en France ?",
    description:
      "Agrément ANJ, mentions obligatoires, vérification d'identité : les critères pour distinguer un site de paris légal d'un site illégal.",
    h1: "Site de paris légal ou illégal : comment le reconnaître ?",
    sections: [
      {
        id: "criteres-legal",
        h2: "Les 6 critères d'un site légal",
        blocks: [
          {
            t: "ol",
            items: [
              "Il figure sur la <strong>liste officielle des opérateurs agréés de l'ANJ</strong>.",
              "Le nom de domaine se termine généralement par <strong>.fr</strong> (les sites agréés proposent leur offre française sur un domaine .fr).",
              "Il affiche le message de prévention et l'interdiction aux mineurs.",
              "Il exige une vérification d'identité avant les retraits.",
              "Il propose des outils de modération (limites de dépôt, auto-exclusion).",
              "Il indique clairement ses mentions légales et son agrément.",
            ],
          },
        ],
      },
      {
        id: "signaux-alerte",
        h2: "Les signaux d'alerte d'un site illégal",
        blocks: [
          {
            t: "list",
            items: [
              "Bonus démesurés, sans conditions claires.",
              "Absence de vérification d'identité, ou paiements en cryptomonnaies uniquement.",
              "Absence de mention de l'ANJ.",
              "Casino en ligne proposé (interdit en France).",
              "Offres non limitées aux compétitions autorisées.",
              "Redirections, sites « miroirs » qui changent d'adresse.",
            ],
          },
        ],
      },
      {
        id: "risques",
        h2: "Quels risques ?",
        blocks: [
          {
            t: "p",
            html: "Sur un site non agréé, <strong>aucune garantie de paiement, aucune protection des données, aucun recours, aucun outil de protection du joueur</strong>. Le site peut disparaître avec vos fonds.",
          },
        ],
      },
      {
        id: "reglementation",
        h2: "La réglementation applicable au joueur",
        blocks: [
          {
            t: "p",
            html: "La loi vise avant tout les opérateurs non agréés. Mais jouer sur un site illégal vous prive de toutes les garanties. Le plus sûr : ne jouer que chez un opérateur agréé.",
          },
          {
            t: "callout",
            kind: "retain",
            title: "Checklist rapide",
            html: "Agréé ANJ ✓ · domaine .fr ✓ · vérification d'identité ✓ · message de prévention ✓ · outils de limitation ✓",
          },
        ],
      },
    ],
  },
  {
    path: "/cadre-legal/regles-du-joueur",
    category: "Cadre légal",
    title: "Les règles du joueur en France : majorité, compte unique, identité",
    description:
      "Âge minimum, compte unique, vérification d'identité, interdit de jeux : ce que la loi impose aux joueurs de paris sportifs.",
    h1: "Les règles à connaître avant de parier",
    sections: [
      {
        id: "majorite",
        h2: "Avoir 18 ans révolus",
        blocks: [
          {
            t: "p",
            html: "Les paris sont interdits aux mineurs, en ligne comme en point de vente. L'opérateur doit vérifier l'âge du joueur.",
          },
        ],
      },
      {
        id: "compte-unique",
        h2: "Un compte unique, personnel",
        blocks: [
          {
            t: "p",
            html: "Chaque joueur ne peut avoir qu'<strong>un seul compte par opérateur</strong>, ouvert à son propre nom, avec sa propre identité et son propre moyen de paiement. Il est interdit de prêter, d'emprunter ou d'ouvrir un compte pour autrui.",
          },
        ],
      },
      {
        id: "kyc",
        h2: "La vérification d'identité (KYC)",
        blocks: [
          {
            t: "p",
            html: "L'opérateur doit vérifier l'identité, l'âge, le domicile et le compte bancaire du joueur. Sans cette vérification complète, <strong>les retraits ne sont pas possibles</strong>.",
          },
        ],
      },
      {
        id: "interdits",
        h2: "Les interdits de jeux",
        blocks: [
          {
            t: "p",
            html: "Une personne peut demander à l'ANJ une <strong>interdiction volontaire de jeux</strong> : elle ne peut plus jouer chez aucun opérateur agréé pendant une durée minimale (de plusieurs années). Certaines personnes sont aussi interdites de jeux par décision judiciaire. Les opérateurs consultent ce fichier.",
          },
        ],
      },
      {
        id: "outils-moderation",
        h2: "Les outils de modération",
        blocks: [
          {
            t: "p",
            html: "Chaque opérateur doit proposer : limites de dépôt, limites de mise, auto-exclusion temporaire, rappel du temps passé.",
          },
        ],
      },
      {
        id: "obligations-operateur",
        h2: "Les obligations de l'opérateur",
        blocks: [
          {
            t: "p",
            html: "Informer sur les risques, ne pas cibler les mineurs, encadrer la publicité, respecter la confidentialité des données.",
          },
        ],
      },
    ],
  },
  {
    path: "/cadre-legal/fiscalite-des-gains",
    category: "Cadre légal",
    title: "Fiscalité des gains aux paris sportifs en France",
    description:
      "Les gains aux paris sportifs sont-ils imposables ? Principes généraux et cas particuliers pour les joueurs en France.",
    h1: "Fiscalité des gains aux paris sportifs",
    sections: [
      {
        id: "avertissement-fiscal",
        h2: "Avant toute chose",
        blocks: [
          {
            t: "callout",
            kind: "warn",
            html: "Cette page fournit des informations générales, sans valeur de conseil fiscal. Pour votre situation, consultez l'administration fiscale ou un professionnel.",
          },
        ],
      },
      {
        id: "joueur-occasionnel",
        h2: "Le principe pour un joueur occasionnel",
        blocks: [
          {
            t: "p",
            html: "Pour un joueur qui parie à titre de loisir, les gains ne sont en principe <strong>pas imposables au titre de l'impôt sur le revenu</strong> : ce sont les opérateurs qui supportent les prélèvements spécifiques sur les mises.",
          },
        ],
      },
      {
        id: "activite-habituelle",
        h2: "Le cas de l'activité habituelle",
        blocks: [
          {
            t: "p",
            html: "Si l'activité présente un caractère habituel et organisé, à but lucratif, l'administration fiscale peut la requalifier. Chaque situation est examinée individuellement.",
          },
        ],
      },
      {
        id: "bonus-et-gains",
        h2: "Bonus, freebets et gains",
        blocks: [
          {
            t: "p",
            html: "Les gains obtenus avec un bonus suivent le même traitement fiscal que les autres gains de jeu.",
          },
        ],
      },
      {
        id: "pertes",
        h2: "Les pertes",
        blocks: [
          { t: "p", html: "Les pertes de jeu ne sont <strong>pas déductibles</strong>." },
        ],
      },
      {
        id: "ou-s-informer",
        h2: "Où s'informer ?",
        blocks: [
          {
            t: "p",
            html: 'Site officiel de l\'administration fiscale (<a href="https://www.impots.gouv.fr" target="_blank" rel="noopener noreferrer nofollow" class="font-semibold text-[#2BB08A] underline transition-colors hover:text-[#0B1F3A]">impots.gouv.fr</a>) et service d\'information des impôts.',
          },
        ],
      },
    ],
  },
];
